/**
 * MM Inspirations - Moon Moon Agency
 * - Mode statique : Splide initialisé uniquement en mobile (≤768px), flex natif sur desktop
 * - Mode S&D : fetch API recommendations, même logique mobile/desktop
 */

/*********************************************************************************************
 * 🎠 SPLIDE MOBILE INIT — mode statique
 */
const InspirationsSliderManager = {
	config: {
		selectors: {
			slider: '[data-inspirations-slider]',
		},
		splideOptions: {
			type: 'slide',
			arrows: false,
			pagination: false,
			focus: 'center',
			start: 1,
			fixedWidth: '200px',
			gap: 10,
			padding: { left: '40px', right: '40px' },
		},
	},

	init() {
		const sliders = window.getElements(this.config.selectors.slider);
		if (!sliders.length) return;

		sliders.forEach((el) => this._setup(el));
	},

	_setup(el) {
		let splideInstance = null;

		const mount = () => {
			if (splideInstance || !window.isMobile()) return;
			this._wrapForSplide(el);
			splideInstance = new Splide(el, this.config.splideOptions).mount();
			window.log('info', '🎠 [INSPIRATIONS_SLIDER]', 'Splide mounted');
		};

		const destroy = () => {
			if (!splideInstance) return;
			splideInstance.destroy(true);
			splideInstance = null;
			this._unwrapFromSplide(el);
			window.log('info', '🎠 [INSPIRATIONS_SLIDER]', 'Splide destroyed');
		};

		mount();

		window.addEventListener('resize', window.debounce(() => {
			if (window.isMobile()) {
				mount();
			} else {
				destroy();
			}
		}, 200));
	},

	_wrapForSplide(el) {
		if (el.querySelector('.splide__track')) return;
		el.classList.add('splide');
		const cards = Array.from(el.children);
		const list = document.createElement('ul');
		list.className = 'splide__list';
		cards.forEach((card) => {
			const li = document.createElement('li');
			li.className = 'splide__slide';
			li.appendChild(card);
			list.appendChild(li);
		});
		const track = document.createElement('div');
		track.className = 'splide__track';
		track.appendChild(list);
		el.appendChild(track);
	},

	_unwrapFromSplide(el) {
		el.classList.remove('splide', 'is-initialized', 'is-active');
		const list = el.querySelector('.splide__list');
		if (!list) return;
		const cards = Array.from(list.querySelectorAll('.splide__slide')).map((li) => li.firstElementChild);
		const track = el.querySelector('.splide__track');
		if (track) track.remove();
		cards.forEach((card) => el.appendChild(card));
	},
};

if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', () => InspirationsSliderManager.init());
} else {
	InspirationsSliderManager.init();
}

/*********************************************************************************************
 * 🎴 CUSTOM ELEMENT — mode Search & Discovery
 */
class MMInspirationsReco extends HTMLElement {
	connectedCallback() {
		if (this._initialized) return;
		this._initialized = true;

		new IntersectionObserver((entries, observer) => {
			if (!entries[0].isIntersecting) return;
			observer.unobserve(this);
			this._load();
		}, { rootMargin: '0px 0px 400px 0px' }).observe(this);
	}

	_load() {
		const productId = this.dataset.productId;
		const limit = parseInt(this.dataset.limit) || 5;
		const rawRoot = this.dataset.root || '/';
		const root = rawRoot.endsWith('/') ? rawRoot : `${rawRoot}/`;
		const discoverText = this.dataset.discoverText || 'Découvrir';
		this._root = root;

		if (!productId) {
			window.log('warn', '🎴 [INSPIRATIONS_RECO]', 'No product id — skipping S&D fetch');
			return;
		}

		fetch(`${root}recommendations/products.json?product_id=${productId}&intent=related&limit=${limit}`)
			.then((res) => {
				if (!res.ok) throw new Error(`HTTP ${res.status}`);
				return res.json();
			})
			.then(({ products }) => {
				if (!products || products.length === 0) {
					this._hide();
					return;
				}

				products.slice(0, limit).forEach((p) => {
					this.insertAdjacentHTML('beforeend', this._renderCard(p, discoverText));
				});

				this.classList.remove('mm-inspirations__cards--loading');

				if (window.isMobile()) {
					this._mountSplide();
				}

				window.addEventListener('resize', window.debounce(() => {
					if (window.isMobile() && !this._splide) {
						this._mountSplide();
					} else if (!window.isMobile() && this._splide) {
						this._destroySplide();
					}
				}, 200));

				window.log('info', '🎴 [INSPIRATIONS_RECO]', 'S&D recommendations loaded', { count: products.length });
			})
			.catch((e) => {
				window.log('warn', '🎴 [INSPIRATIONS_RECO]', 'Fetch failed', { error: e.message });
				this._hide();
			});
	}

	_mountSplide() {
		if (this._splide || !window.Splide) return;
		const cards = Array.from(this.children);
		const list = document.createElement('ul');
		list.className = 'splide__list';
		cards.forEach((card) => {
			const li = document.createElement('li');
			li.className = 'splide__slide';
			li.appendChild(card);
			list.appendChild(li);
		});
		const track = document.createElement('div');
		track.className = 'splide__track';
		track.appendChild(list);
		this.classList.add('splide');
		this.appendChild(track);
		this._splide = new Splide(this, {
			type: 'slide',
			arrows: false,
			pagination: false,
			focus: 'center',
			start: 1,
			fixedWidth: '200px',
			gap: 10,
			padding: { left: '40px', right: '40px' },
		}).mount();
	}

	_destroySplide() {
		if (!this._splide) return;
		this._splide.destroy(true);
		this._splide = null;
		this.classList.remove('splide', 'is-initialized', 'is-active');
		const list = this.querySelector('.splide__list');
		if (!list) return;
		const cards = Array.from(list.querySelectorAll('.splide__slide')).map((li) => li.firstElementChild);
		const track = this.querySelector('.splide__track');
		if (track) track.remove();
		cards.forEach((card) => this.appendChild(card));
	}

	_renderCard(product, discoverText) {
		const image = product.featured_image
			? `<img src="${product.featured_image}" alt="${this._escape(product.title)}" class="mm-card-inspiration__image" loading="lazy" decoding="async">`
			: '';
		const price = product.price_min != null ? this._formatMoney(product.price_min) : '';
		// L'API recommendations/products.json renvoie une URL sans préfixe de locale même quand
		// la requête est locale-aware : on la reconstruit depuis le handle pour rester sur la bonne langue.
		const url = `${this._root}products/${product.handle}`;

		return `
			<a href="${url}" class="mm-card-inspiration" aria-label="${this._escape(product.title)}">
				<div class="mm-card-inspiration__image-wrapper">${image}</div>
				${this._renderWishlist(product)}
				<div class="mm-card-inspiration__content">
					<div class="mm-card-inspiration__top">
						<span class="mm-card-inspiration__price">${price}</span>
					</div>
					<div class="mm-card-inspiration__cta mm-btn mm-btn-primary mm-btn-small mm-w-full mm-text-sm mm-bg-white">
						<span class="mm-btn__mask">
							<span class="mm-btn__texts">
								<span class="mm-btn__text"><span data-btn-label>${discoverText}</span></span>
								<span class="mm-btn__text" aria-hidden="true"><span data-btn-label>${discoverText}</span></span>
							</span>
						</span>
					</div>
				</div>
			</a>`;
	}

	_renderWishlist(product) {
		const variantId = product.variants?.[0]?.id ?? '';
		return `
			<div class="mm-wishlist mm-card-inspiration__wishlist" data-product-handle="${this._escape(product.handle)}" data-wishlist-button>
				<div class="mm-wishlist-icon-container">
					<ooo-wl-wishlist-button product-id="${product.id}" handle="${this._escape(product.handle)}" variant-id="${variantId}" loading>
						<button type="button">
							${this._wishlistIcon()}
						</button>
					</ooo-wl-wishlist-button>
				</div>
			</div>`;
	}

	_wishlistIcon() {
		return `<svg class="mm-icon-wishlist" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
			<path d="M14.6673 5.90815C14.6673 6.93913 14.2715 7.9294 13.5645 8.66193C11.9373 10.3487 10.3589 12.1075 8.67085 13.7331C8.28392 14.1003 7.67012 14.0869 7.29985 13.7031L2.43649 8.66193C0.966483 7.13813 0.966483 4.67815 2.43649 3.15438C3.92094 1.61563 6.33928 1.61563 7.82372 3.15438L8.00052 3.33761L8.17718 3.15449C8.88892 2.41633 9.85825 2 10.8708 2C11.8834 2 12.8527 2.41629 13.5645 3.15438C14.2715 3.88697 14.6673 4.87718 14.6673 5.90815Z" stroke="var(--black-900)" stroke-width="1.2" stroke-linejoin="round"/>
		</svg>`;
	}

	_formatMoney(cents) {
		const amount = (cents / 100).toFixed(2).replace('.', ',').replace(/,00$/, '');
		return `${amount} €`;
	}

	_escape(str) {
		return String(str)
			.replace(/&/g, '&amp;')
			.replace(/"/g, '&quot;')
			.replace(/</g, '&lt;')
			.replace(/>/g, '&gt;');
	}

	_hide() {
		this.closest('.mm-inspirations')?.style.setProperty('display', 'none');
	}
}

if (!customElements.get('mm-inspirations-reco')) {
	customElements.define('mm-inspirations-reco', MMInspirationsReco);
}
